<template>
  <v-container fluid class="sectDist py-12 mt-15">
    <v-row justify="center" class="mb-12 mx-4 px-0" >
      <div class="text-center">
        <h1 class="text-h3 font-weight-bold primary--text mb-4">Fonduri de finanțare</h1>
        <v-divider class="mx-auto mb-4" style="width: 140px; border-width: 4px;"></v-divider>
        <p class="text-subtitle-1 grey--text text--darken-2">
          Evoluția costului standard per elev și a principalelor categorii de cheltuieli
        </p>
      </div>
    </v-row>

    <v-timeline 
      align-top 
      dense 
      class="timeline-custom mx-auto"
      style="max-width: 740px;"
    >
      <v-timeline-item
        v-for="(item, i) in sortedList"
        :key="i"
        :color="item.color"
        small
        fill-dot
      >
        <template v-slot:icon>
          <v-icon color="white" size="20">
            {{ getIconForYear(item.date) }}
          </v-icon>
        </template>

        <v-card 
          class="elevation-3 pa-6 rounded-xl mx-auto"
          max-width="580"
          color="white"
          :style="`border: 3.5px solid ${item.colorValue}; border-radius: 20px;`"
        >
          <v-card-title class="text-h5 font-weight-medium pa-0 mb-5 justify-center flex-wrap">
            <span class="mr-4 text-center">{{ item.date }}</span>
            <v-chip 
              :color="item.color" 
              text-color="white"
              class="font-weight-bold px-4"
              label
              medium
            >
              {{ formatTotal(item.description) || '—' }}
            </v-chip>
          </v-card-title>

          <v-card-text class="pa-0 text-body-1 black--text">
            <div v-if="hasBreakdown(item.description)" class="mb-5">
              <!-- Newer years (2025, 2024) - only two categories -->
              <template v-if="isAggregatedFormat(item.description)">
                <v-row dense class="mb-3 align-center">
                  <v-col cols="5" sm="4" class="font-weight-medium text-body-2 grey--text text--darken-3">
                    Salarii și taxe
                  </v-col>
                  <v-col cols="7" sm="8" class="text-right font-weight-medium text-body-1">
                    {{ formatRON(getAggregatedSalariiTaxe(item.description)) }}
                  </v-col>
                </v-row>

                <v-row dense class="mb-4 align-center">
                  <v-col cols="5" sm="4" class="font-weight-medium text-body-2 grey--text text--darken-3">
                    Bunuri & servicii
                  </v-col>
                  <v-col cols="7" sm="8" class="text-right font-weight-medium text-body-1">
                    {{ formatRON(getBunuriServicii(item.description)) }}
                  </v-col>
                </v-row>
              </template>

              <!-- Older years - detailed breakdown (3 lines) -->
              <template v-else>
                <v-row dense class="mb-3 align-center">
                  <v-col cols="5" sm="4" class="font-weight-medium text-body-2 grey--text text--darken-3">
                    Salarii
                  </v-col>
                  <v-col cols="7" sm="8" class="text-right font-weight-medium text-body-1">
                    {{ formatRON(getSalarii(item.description)) }}
                  </v-col>
                </v-row>

                <v-row dense class="mb-3 align-center">
                  <v-col cols="5" sm="4" class="font-weight-medium text-body-2 grey--text text--darken-3">
                    Taxe
                  </v-col>
                  <v-col cols="7" sm="8" class="text-right font-weight-medium text-body-1">
                    {{ formatRON(getTaxe(item.description)) }}
                  </v-col>
                </v-row>

                <v-row dense class="mb-4 align-center">
                  <v-col cols="5" sm="4" class="font-weight-medium text-body-2 grey--text text--darken-3">
                    Bunuri & servicii
                  </v-col>
                  <v-col cols="7" sm="8" class="text-right font-weight-medium text-body-1">
                    {{ formatRON(getBunuriServicii(item.description)) }}
                  </v-col>
                </v-row>
              </template>

              <v-divider class="my-4"></v-divider>
            </div>

            <div class="text-caption text-right grey--text text--darken-2 mt-3">
              Total finanțare: 
              <strong class="black--text">{{ formatTotal(item.description) || '—' }}</strong>
            </div>
          </v-card-text>
        </v-card>
      </v-timeline-item>
    </v-timeline>

    
  </v-container>
</template>

<script>
export default {
  name: "Funds",
  data() {
    return {
      colorPalette: [
        'orange darken-2',        // warm orange
        'green darken-1',  // soft coral
        'blue lighten-1',        // sunny amber
        'purple lighten-1',       // gentle yellow
        'red lighten-2',          // warm red-pink
      ],
      list: [
        { date: "2025", description: "de finantare in baza costului standard per elev in valoare de 828000 Ron (salarii, taxe), 76000 bunuri si servicii. Total 904000 Ron." },
        { date: "2024", description: "de finantare in baza costului standard per elev in valoare de 870000 Ron (salarii, taxe), 70000 bunuri si servicii. Total 940000 Ron." },
        { date: "2023", description: "de finantare in baza costului standard per elev in valoare de 731000 Ron din care 403382 salarii, 257618 taxe, 70000 bunuri si servicii." },
        { date: "2022", description: "de finantare in baza costului standard per elev in valoare de 816000 Ron din care 423364 salarii, 323636 taxe, 69000 bunuri si servicii." },
        { date: "2021", description: "de finantare in baza costului standard per elev in valoare de 580000 Ron din care 309888 salarii, 225112 taxe, 45000 bunuri si servicii." },
        { date: "2020", description: "de finantare in baza costului standard per elev in valoare de 720000 Ron din care 400450 salarii, 273550 taxe, 46000 bunuri si servicii." },
        { date: "2019", description: "de finanțare in baza costului standard per elev in valoare de 417000 RON, din care 226858 salarii, 165142 taxe, 25000 bunuri si servicii." },
        { date: "2018", description: "de finantare in baza costului standard per elev in valoare de 210000 Ron din care 113107 salarii, 80893 taxe, 16000 bunuri si servicii." },
        { date: "2017", description: "de finantare in baza costului standard per elev in valoare de 176000 Ron din care 163000 taxe salariale si 13000 bunuri si servicii." },
      ],
    };
  },
  computed: {
    sortedList() {
      const sorted = [...this.list].sort((a, b) => Number(b.date) - Number(a.date));
      
      return sorted.map((item, index) => {
        const colorIndex = index % this.colorPalette.length;
        const color = this.colorPalette[colorIndex];
        
        return {
          ...item,
          color,
          colorValue: this.getColorHex(color),
        };
      });
    },
  },
  methods: {
    getIconForYear(year) {
      const y = Number(year);
      if (y >= 2025) return "mdi-finance";
      if (y >= 2022) return "mdi-school";
      if (y >= 2019) return "mdi-chart-line";
      return "mdi-cash-multiple";
    },

    formatTotal(text) {
      const match = text.match(/Total\s*(\d+)\s*Ron/i) || text.match(/valoare de\s*(\d+)\s*Ron/i);
      return match ? `${Number(match[1]).toLocaleString('ro-RO')} RON` : '';
    },

    hasBreakdown(text) {
      return /din care|\(salarii|salarii|taxe|bunuri/i.test(text);
    },

    // ───────────────────────────────────────────────
    // New: detect if it's the aggregated format (2025 & 2024 style)
    // ───────────────────────────────────────────────
    isAggregatedFormat(text) {
      return /\(\s*salarii\s*,\s*taxe\s*\)/i.test(text);
    },

    // For newer years: extract the amount before "(salarii, taxe)"
    getAggregatedSalariiTaxe(text) {
      const match = text.match(/(\d+)\s*Ron\s*\(\s*salarii\s*,\s*taxe\s*\)/i);
      return match ? Number(match[1]) : null;
    },

    // Common – bunuri si servicii (used in both formats)
    getBunuriServicii(text) {
      const match = text.match(/(\d+)\s*bunuri\s*(?:si|și)\s*servicii/i);
      return match ? Number(match[1]) : null;
    },

    // Only for older years (detailed breakdown)
    getSalarii(text) {
      const match = text.match(/(\d+)\s*(?:salarii|salarii\s*,)/i);
      return match ? Number(match[1]) : null;
    },

    getTaxe(text) {
      const match = text.match(/(\d+)\s*(?:taxe|taxe\s*,)/i);
      return match ? Number(match[1]) : null;
    },

    formatRON(value) {
      if (value === null || isNaN(value)) return '—';
      return value.toLocaleString('ro-RO') + ' RON';
    },

    getColorHex(colorName) {
      const hexMap = {
        'orange darken-2':       '#f57c00',
        'green darken-1': '#ff7043',
        'blue lighten-1':       '#ffb74d',
        'purple lighten-1':      '#fff176',
        'red lighten-2':         '#ff8a80',
      };
      return hexMap[colorName] || '#ff9800';
    },
  },
};
</script>

<style scoped>
.timeline-custom {
  padding: 0 16px;
}

.timeline-custom :deep(.v-timeline-item__body) {
  padding-left: 32px !important;
  padding-right: 32px !important;
}

.timeline-custom :deep(.v-timeline-item__dot) {
  border-width: 5px !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.14) !important;
}

.timeline-custom :deep(.v-timeline-divider__line) {
  border-color: rgba(0,0,0,0.06) !important;
}

.v-card {
  transition: all 0.28s ease;
}

.v-card:hover {
  box-shadow: 0 12px 32px rgba(0,0,0,0.13) !important;
  transform: translateY(-5px);
}
</style>